/**
 * The routes the Efkt tree can serve, spelled the way Next writes them into `router.pathname` —
 * so dynamic segments keep their `[id]` placeholder.
 *
 * **These are this app's ordinary paths, not a prefix.** There is no `/v2` URL anywhere and nothing
 * redirects: both trees answer on the same paths, and this list only says *which* of those paths the
 * Efkt tree is able to serve. `pages/_app.tsx` mounts the Efkt shell when the flag is on *and* the
 * current path appears below, and falls through to the pre-Efkt shell otherwise. `v2` survives as a
 * directory name only, because that is where this tree's files live until cutover.
 *
 * **This module must keep importing nothing.** `pages/_app.tsx` imports it statically to make that
 * decision, so anything pulled in here lands in the `_app` chunk that every page downloads — which
 * is exactly what `dynamic(() => import('v2/App'))` exists to avoid.
 *
 * A route is missing from this list for one of two reasons, both of which mean "keep the pre-Efkt
 * page":
 *
 * - **Most public pages** (`/accept-invite`, `/system-notification`) have no signed-in user, so
 *   there is no per-user flag to read, and no v2 page has been built for them yet. `/booking` and
 *   `/presentation` are the two exceptions — see below.
 * - **`/active-directory`** is dropped from this tree outright, not merely unbuilt — see below.
 *
 * **The three sign-in pages are in the list, and they are public routes.** `/login`, `/esoft` and
 * `/auth-redirect` run with no signed-in user, so there is no per-user flag to evaluate and
 * `feature.flag.tsx` resolves them straight to `newUiEnabled: true`, exactly as it does `/booking`.
 * Which backend answers a given sign-in is decided per call, at the call site
 * (`v2/modules/auth/hooks.ts`), not by a flag — Efkt and datahub hand out the same B2C journey from
 * the same tenant, so either answer works and the resulting token validates against both.
 *
 * **`/active-directory` is deliberately NOT here, and this tree has no copy of that page.** Efkt is
 * B2C-only and honours no impersonation header, so the department/colleague picker has nothing to
 * act on (meeting 2026-09-22). The path keeps answering from the pre-Efkt tree, which is what
 * `website-employee-portal` hardcodes (`src/constants/server/api.ts`) and what an Esoft employee
 * still needs; a migrated user never reaches it and lands on the dashboard instead.
 *
 * **The URLs do not change and nothing is added to Azure.** `/auth-redirect` stays the exact URI
 * whitelisted in app registration `4d59ca5a-6955-4735-b496-155f28bd4076` — an earlier design
 * prefixed it and got `AADSTS50011`. Both trees answer on the same paths, so moving these routes
 * here changes which component renders and nothing else.
 *
 * **`/booking` has no signed-in user either, but is included below anyway.** Unlike the private
 * routes, a public route has no account to gate a rollout by, so `feature.flag.tsx` doesn't try to
 * flag one at all — `isPublicRoute` routes are resolved straight to `newUiEnabled: true`. Anything
 * backend-specific this page needs to decide (which backend a given booking lives on) is the page's
 * own job, done per record in `BookingPageContent/hooks.ts` (try Efkt, fall back to datahub) — not a
 * page-level flag.
 *
 * **`/presentation` is included for a different reason: it is the app's one server-rendered page.**
 * `newUiEnabled` alone would put every visitor here through the Efkt shell regardless of which
 * backend actually owns the case, so `pages/_app.tsx`'s `App` component special-cases this one route
 * and reads `pageProps.sourceBackend` — resolved server-side, per request, by `getServerSideProps`
 * (`pages/presentation.tsx`, a rare permissioned exception to the pre-Efkt tree's read-only rule —
 * see `CLAUDE.md`) — instead of `newUiEnabled`. This list only says the Efkt tree *can* serve
 * `/presentation`; whether a given request actually does is decided per request, not here.
 *
 * At cutover this file is deleted together with `feature.flag.ts`.
 */
export const EFKT_ROUTES = [
	'/',
	'/auth-redirect',
	'/booking',
	'/cases',
	'/cases/[id]',
	'/cases/[id]/details',
	'/cases/[id]/orders',
	'/esoft',
	'/feedback',
	'/login',
	'/new-order',
	'/presentation',
	'/questionnaire',
	'/interview',
	'/receipt',
	'/redirect/new-order-for-case',
	'/support',
	'/tasks',
	'/users',
] as const

export type EfktRouteType = (typeof EFKT_ROUTES)[number]

/**
 * Routes that run with **no signed-in user**, and therefore have no feature flag to evaluate.
 *
 * This is the other half of the shell decision and it is a different question from `EFKT_ROUTES`
 * above: that list says which paths the Efkt tree *can* serve, this one says which of them can be
 * served without first resolving who is asking. The two lists overlap but neither contains the
 * other — `/accept-invite`, `/active-directory` and `/system-notification` are public and still on
 * the pre-Efkt tree, while `/cases` is Efkt-eligible and strictly private.
 *
 * It lives here rather than in `feature.flag.tsx` because keeping it there made it a second,
 * hand-maintained list that had to stay in step with this one: a route added above but forgotten
 * below resolves no flag, never settles, and leaves the app blank rather than rendering anything —
 * a failure with no error attached to it. One file, one decision, no sync to remember.
 *
 * Three groups, for three different reasons:
 *
 * - **Anonymous content** (`/booking`, `/presentation`, `/questionnaire`, `/interview`,
 *   `/accept-invite`, `/system-notification`) — nobody is signed in, and the page decides anything
 *   backend-specific itself, per record, the way `BookingPageContent/hooks.ts` does.
 * - **The sign-in group** (`/login`, `/esoft`, `/auth-redirect`) — these run *before* there is a
 *   token, so there is nothing to resolve a flag from by definition.
 */
export const PUBLIC_ROUTES = [
	'/accept-invite',
	'/auth-redirect',
	'/booking',
	'/esoft',
	'/interview',
	'/login',
	'/presentation',
	'/questionnaire',
	'/system-notification',
] as const

/** Whether the route runs with no signed-in user, so no feature flag applies to it. */
export function isPublicRoute(pathname: string): boolean {
	return (PUBLIC_ROUTES as readonly string[]).includes(pathname)
}

/**
 * Narrows to `EfktRouteType`, which is what makes the page map in `App.tsx` exhaustive: that map is
 * typed `Record<EfktRouteType, ...>`, so adding a route here without adding its page is a type error
 * rather than a blank screen.
 */
export function isEfktRoute(pathname: string): pathname is EfktRouteType {
	return (EFKT_ROUTES as readonly string[]).includes(pathname)
}
